import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Textarea,
  Button,
  Group,
  Alert,
  Paper,
  Title,
  Badge,
  LoadingOverlay
} from '@mantine/core';
import { IconCheck, IconX, IconAlertCircle, IconClipboardCheck } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  yearlyCalendarSchedulePlanService,
  ApprovalData
} from 'services/yearlyCalendar/schedulePlanService';

interface ApprovalModalProps {
  opened: boolean;
  onClose: () => void;
  planningId: string;
  planningName: string;
  planningDescription?: string;
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  opened,
  onClose,
  planningId,
  planningName,
  planningDescription
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  // Approval mutation
  const approvalMutation = useMutation({
    mutationFn: (approvalData: ApprovalData) =>
      yearlyCalendarSchedulePlanService.approveSchedulePlan(approvalData),
    onSuccess: (data, variables) => {
      notifications.show({
        title: 'Success',
        message: `Schedule plan ${variables.status.toLowerCase()} successfully`,
        color: variables.status === 'Approved' ? 'green' : 'orange',
        icon: variables.status === 'Approved' ? <IconCheck size={16} /> : <IconX size={16} />
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['yearlyCalendarSchedulePlanDetail', planningId] });
      queryClient.invalidateQueries({ queryKey: ['yearlyCalendarSchedulePlans'] });

      // Reset form and close modal
      setNotes('');
      setIsSubmitting(false);
      onClose();
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.message || 'Failed to process approval',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
      setIsSubmitting(false);
    }
  });

  const handleApproval = (status: 'Approved' | 'Rejected') => {
    if (!notes.trim()) {
      notifications.show({
        title: 'Required Field',
        message: 'Please provide notes for the approval decision',
        color: 'yellow',
        icon: <IconAlertCircle size={16} />
      });
      return;
    }

    setIsSubmitting(true);

    const approvalData: ApprovalData = {
      msLCMSPlanningID: planningId,
      status,
      notes: notes.trim()
    };

    approvalMutation.mutate(approvalData);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setNotes('');
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap='xs'>
          <IconClipboardCheck size={20} />
          <Text fw={600}>Schedule Plan Approval</Text>
        </Group>
      }
      size='md'
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <LoadingOverlay visible={isSubmitting} />

      <Stack gap='md'>
        {/* Planning Details */}
        <Paper p='md' withBorder bg='gray.0'>
          <Stack gap='xs'>
            <Group justify='space-between' align='flex-start'>
              <div>
                <Title order={4} lineClamp={2}>
                  {planningName}
                </Title>
                {planningDescription && (
                  <Text size='sm' c='dimmed' mt='xs' lineClamp={3}>
                    {planningDescription}
                  </Text>
                )}
              </div>
              <Badge variant='light' color='blue' size='sm'>
                Pending Approval
              </Badge>
            </Group>
          </Stack>
        </Paper>

        {/* Alert Info */}
        <Alert icon={<IconAlertCircle size={16} />} color='blue' variant='light'>
          <Text size='sm'>
            Please review the schedule plan and provide your decision along with notes. This action
            cannot be undone.
          </Text>
        </Alert>

        {/* Notes Input */}
        <Stack gap='xs'>
          <Text size='sm' fw={500}>
            Approval Notes{' '}
            <Text component='span' c='red'>
              *
            </Text>
          </Text>
          <Textarea
            placeholder='Enter your notes regarding this approval decision...'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            minRows={4}
            maxRows={8}
            autosize
            disabled={isSubmitting}
            error={!notes.trim() && notes.length > 0 ? 'Notes are required' : null}
          />
          <Text size='xs' c='dimmed'>
            Minimum 10 characters required
          </Text>
        </Stack>

        {/* Action Buttons */}
        <Group justify='flex-end' mt='md'>
          <Button variant='outline' onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button
            color='red'
            leftSection={<IconX size={16} />}
            onClick={() => handleApproval('Rejected')}
            disabled={isSubmitting || !notes.trim() || notes.trim().length < 10}
            loading={isSubmitting && approvalMutation.variables?.status === 'Rejected'}
          >
            Reject
          </Button>

          <Button
            color='green'
            leftSection={<IconCheck size={16} />}
            onClick={() => handleApproval('Approved')}
            disabled={isSubmitting || !notes.trim() || notes.trim().length < 10}
            loading={isSubmitting && approvalMutation.variables?.status === 'Approved'}
          >
            Approve
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ApprovalModal;
